// ToolCallTrace — one collapsible row in the read-only agent's tool-call trace.
//
// Renders the prettified tool name, an outcome badge, and (when expanded) the
// exact arguments, the permission reason for a denied call, and the result
// summary. View-model + status mapping live in services/agentConsole.ts so this
// module only exports the component (ESLint react-refresh/only-export-components).

import { useState } from 'react';
import { StatusBadge } from '../common/StatusBadge';
import {
  toolStatusLabel,
  toolStatusVariant,
  type ToolCallView,
} from '../../services/agentConsole';
import { useIsAdmin } from '../../context/AuthContext';
import { friendlyToolLabel } from '../../services/toolLabels';
import styles from './ToolCallTrace.module.css';

interface ToolCallTraceProps {
  tool: ToolCallView;
}

function inputPreview(input: Record<string, unknown>): string {
  if (typeof input.sql === 'string') return input.sql;
  if (typeof input.query === 'string') return input.query;
  const keys = Object.keys(input);
  if (keys.length === 0) return '';
  return keys.join(', ');
}

function formatInput(input: Record<string, unknown>): string {
  if (typeof input.sql === 'string') return input.sql;
  if (typeof input.query === 'string' && Object.keys(input).length === 1) {
    return input.query;
  }
  return JSON.stringify(input, null, 2);
}

export function ToolCallTrace({ tool }: ToolCallTraceProps) {
  const isAdmin = useIsAdmin();
  const [expanded, setExpanded] = useState(false);

  // Non-admins get a plain-language step in place of the raw tool name, SQL,
  // arguments, KB id and status — driven by the global services/toolLabels.ts
  // dictionary. Internal-plumbing tools (voice_reply, ToolSearch, …) drop out.
  if (!isAdmin) {
    const friendly = friendlyToolLabel(tool);
    if (!friendly) return null;
    return (
      <div className={styles.friendlyRow}>
        <span className={styles.friendlyIcon} aria-hidden="true">
          {friendly.icon}
        </span>
        <span className={styles.friendlyLabel}>{friendly.label}</span>
      </div>
    );
  }

  const preview = inputPreview(tool.input);
  const hasDetail =
    Object.keys(tool.input).length > 0 ||
    tool.resultSummary.length > 0 ||
    (tool.status === 'denied' && !!tool.reason);

  return (
    <div className={`${styles.row} ${expanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        disabled={!hasDetail}
      >
        <svg
          className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className={styles.toolName}>{tool.name}</span>
        {tool.kb && (
          <span className={styles.kbBadge} title={`Knowledge Bank: ${tool.kb}`}>
            <svg
              className={styles.kbIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            {tool.kb}
          </span>
        )}
        <StatusBadge variant={toolStatusVariant(tool.status)}>
          {toolStatusLabel(tool.status)}
        </StatusBadge>
        {preview && <span className={styles.preview}>{preview}</span>}
        {tool.durationMs != null && (
          <span className={styles.duration}>{tool.durationMs} ms</span>
        )}
      </button>

      {expanded && (
        <div className={styles.body}>
          {Object.keys(tool.input).length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>Input</div>
              <pre className={styles.code}>{formatInput(tool.input)}</pre>
            </div>
          )}

          {tool.status === 'denied' && tool.reason && (
            <div className={`${styles.section} ${styles.denied}`}>
              <div className={styles.sectionLabel}>Denied</div>
              <p className={styles.reason}>{tool.reason}</p>
            </div>
          )}

          {tool.resultSummary && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                {tool.isError ? 'Error' : 'Result'}
              </div>
              <pre className={`${styles.code} ${tool.isError ? styles.errorCode : ''}`}>
                {tool.resultSummary}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
